import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import UploadOverlay from '../UploadOverlay/UploadOverlay';
import './SharedCollectionScreen.css';

const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

const GALLERY_CATEGORIES = [
  { value: 'guitar', label: '🎸 Guitar' },
  { value: 'pedal', label: '🎛️ Pedal' },
  { value: 'cab', label: '🔊 Cabinet' },
  { value: 'pedalboard', label: '🎚️ Pedalboard' },
  { value: 'live', label: '🎤 Live Shot' },
  { value: 'studio', label: '🎧 Studio' },
  { value: 'other', label: '📷 Other' },
];

function extractYouTubeId(url) {
  if (!url) return null;
  const m = String(url).match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

const SharedCollectionScreen = ({ editRoute = false }) => {
  const { shareToken } = useParams();
  const { user, token } = useAuth();

  // ── Data state ───────────────────────────────────────────────────────
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxItem, setLightboxItem] = useState(null); // { type: 'gallery'|'artifact', ... }

  // ── Owner / Edit mode state ─────────────────────────────────────────
  const [isOwner, setIsOwner] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveFlash, setSaveFlash] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Editable fields
  const [editTagline, setEditTagline] = useState('');
  const [editBio, setEditBio] = useState('');
  const [editSlug, setEditSlug] = useState('');
  const [editBgStyle, setEditBgStyle] = useState('cover');
  const [editBgOverlay, setEditBgOverlay] = useState(0.70);
  const [editShowStats, setEditShowStats] = useState(true);
  const [editShowGallery, setEditShowGallery] = useState(true);
  const [editShowAmps, setEditShowAmps] = useState(true);

  // Inline editing focus
  const [editingField, setEditingField] = useState(null);
  const taglineRef = useRef(null);
  const bioRef = useRef(null);

  // Image upload refs
  const heroInputRef = useRef(null);
  const bgInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // Gallery edit
  const [editingGalleryItem, setEditingGalleryItem] = useState(null);
  const [galleryEditForm, setGalleryEditForm] = useState({ title: '', caption: '', category: 'other' });

  // Card layout — unified ordering of amps + gallery photos in the card grid
  const [cardLayout, setCardLayout] = useState(null);  // [{type:'amp',id:3},{type:'photo',id:12},...]

  // Drag and drop — live reordering
  const [dragType, setDragType] = useState(null);       // 'cards' | 'gallery-to-cards' | null
  const [galleryDragId, setGalleryDragId] = useState(null); // gallery_item_id being dragged from gallery
  const [resizingCardId, setResizingCardId] = useState(null); // gallery_item_id with open size picker
  const preDragData = useRef(null);                       // snapshot before drag started
  const galleryDragItem = useRef(null);                    // gallery item object being dragged
  const cardLayoutRef = useRef(cardLayout);                 // always-current cardLayout for dragEnd closures
  cardLayoutRef.current = cardLayout;
  const dragCardIdRef = useRef(null);                       // { type, id } of the card being dragged
  const dragTypeRef = useRef(null);                         // always-current dragType
  const fileDragCounter = useRef(0);                        // counter for file drag enter/leave

  // File drag-and-drop onto gallery
  const [fileDragOver, setFileDragOver] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [youtubeInputOpen, setYoutubeInputOpen] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(null);

  // Sharing controls (only used in edit mode)
  const [shareOpen, setShareOpen] = useState(false);
  const [shareSettings, setShareSettings] = useState(null); // { share_enabled, share_token, share_url }
  const [shareLoading, setShareLoading] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  // ── Fetch showcase data ──────────────────────────────────────────────
  const fetchShowcase = useCallback(async () => {
    if (!shareToken) return null;
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(shareToken);

    let response;
    try {
      if (isUUID) {
        response = await fetch(`${API_URL}/showcase/by-token/${shareToken}`);
      } else {
        response = await fetch(`${API_URL}/showcase/by-slug/${shareToken}`);
      }

      if (response.ok) {
        const json = await response.json();
        setData(json);
        return json;
      } else if (response.status === 404) {
        // If the user is the owner, sharing may just be disabled — try the owner preview
        if (token) {
          try {
            const ownerResp = await fetch(`${API_URL}/showcase/my/preview`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (ownerResp.ok) {
              const json = await ownerResp.json();
              setData(json);
              return json;
            }
          } catch (e) { /* fall through */ }
        }
        // Legacy share token fallback
        if (isUUID) {
          const legacyResp = await fetch(`${API_URL}/collections/shared/${shareToken}`);
          if (legacyResp.ok) {
            const json = await legacyResp.json();
            const wrapped = {
              owner: json.owner,
              showcase: { has_hero_image: false, has_bg_image: false, show_stats: true, show_amps: true, show_gallery: false, sections_order: 'hero,stats,amps,gallery' },
              gallery: [],
              items: json.items,
              stats: json.stats
            };
            setData(wrapped);
            return wrapped;
          }
        }
        setError('not_found');
      } else {
        setError(response.status === 404 ? 'not_found' : 'server_error');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('network_error');
    } finally {
      setLoading(false);
    }
    return null;
  }, [shareToken, token]);

  useEffect(() => {
    const init = async () => {
      const result = await fetchShowcase();
      if (result) {
        // Initialize card layout (affects both owner and public rendering)
        const sc = result.showcase || {};
        if (sc.card_layout && Array.isArray(sc.card_layout) && sc.card_layout.length > 0) {
          setCardLayout(sc.card_layout);
        }
        // else leave null — rendering falls back to showing all amps

        if (user) {
          const ownerId = result.owner?.user_id;
          const currentUserId = user.user_id || user.userId;
          if (ownerId && currentUserId && ownerId === currentUserId) {
            setIsOwner(true);
            // Only enter edit mode when on the /edit route
            if (editRoute) {
              setEditMode(true);
            }
            setEditTagline(sc.tagline || '');
            setEditBio(sc.bio || '');
            setEditSlug(sc.custom_slug || '');
            setEditBgStyle(sc.bg_style || 'cover');
            setEditBgOverlay(parseFloat(sc.bg_overlay) || 0.70);
            setEditShowStats(sc.show_stats !== false);
            setEditShowGallery(sc.show_gallery !== false);
            setEditShowAmps(sc.show_amps !== false);
            // Eagerly fetch share settings so the Share panel works
            try {
              const shareResp = await fetch(`${API_URL}/collections/share/settings`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (shareResp.ok) {
                const shareData = await shareResp.json();
                setShareSettings(shareData);
              }
            } catch (e) { /* silent */ }
          }
        }
      }
    };
    init();
  }, [fetchShowcase, user, editRoute, token]);

  // ── Sync editMode when navigating between /edit and public routes ───
  useEffect(() => {
    if (isOwner) {
      setEditMode(editRoute);
    }
  }, [editRoute, isOwner]);

  // ── Page title ───────────────────────────────────────────────────────
  useEffect(() => {
    if (data?.owner) {
      const name = data.owner.display_name || data.owner.username;
      const tagline = data.showcase?.tagline;
      document.title = tagline
        ? `${name}'s Collection — ${tagline} | AmpNebula`
        : `${name}'s Amp Collection — AmpNebula`;
    }
    return () => { document.title = 'AmpNebula'; };
  }, [data]);

  // Escape key handler
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        if (resizingCardId) setResizingCardId(null);
        else if (shareOpen) setShareOpen(false);
        else if (lightboxItem) setLightboxItem(null);
        else if (editingGalleryItem) setEditingGalleryItem(null);
        else if (editingField) setEditingField(null);
        else if (settingsOpen) setSettingsOpen(false);
      }
    };
    const clickOutside = (e) => {
      if (resizingCardId && !e.target.closest('.photo-card-size-menu') && !e.target.closest('.photo-card-resize-btn')) {
        setResizingCardId(null);
      }
      if (shareOpen && !e.target.closest('.wysiwyg-share-panel') && !e.target.closest('.wysiwyg-btn-share')) {
        setShareOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    document.addEventListener('mousedown', clickOutside);
    return () => { window.removeEventListener('keydown', handler); document.removeEventListener('mousedown', clickOutside); };
  }, [lightboxItem, editingGalleryItem, editingField, settingsOpen, resizingCardId, shareOpen]);

  // ── WYSIWYG: Save all settings ──────────────────────────────────────
  const publishChanges = async () => {
    if (!token) return;
    setSaving(true);
    setSaveFlash('');
    try {
      const resp = await fetch(`${API_URL}/showcase/my`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tagline: editTagline || null,
          bio: editBio || null,
          custom_slug: editSlug || null,
          bg_style: editBgStyle,
          bg_overlay: editBgOverlay,
          show_stats: editShowStats,
          show_gallery: editShowGallery,
          show_amps: editShowAmps
        })
      });
      if (resp.ok) {
        const updated = await resp.json();
        setData(prev => prev ? {
          ...prev,
          showcase: {
            ...prev.showcase,
            tagline: updated.tagline, bio: updated.bio, custom_slug: updated.custom_slug,
            bg_style: updated.bg_style, bg_overlay: parseFloat(updated.bg_overlay),
            show_stats: updated.show_stats, show_gallery: updated.show_gallery, show_amps: updated.show_amps,
            has_hero_image: updated.has_hero_image, has_bg_image: updated.has_bg_image
          }
        } : prev);
        setDirty(false);
        setSaveFlash('saved');
        setTimeout(() => setSaveFlash(''), 3000);
      } else {
        const err = await resp.json().catch(() => ({}));
        alert(err.error || 'Failed to save');
        setSaveFlash('error');
      }
    } catch (err) {
      console.error('Save error:', err);
      setSaveFlash('error');
    } finally {
      setSaving(false);
    }
  };

  // ── WYSIWYG: Image upload handlers ──────────────────────────────────
  const uploadImage = async (type, file) => {
    setImageUploading(true);
    setUploadMessage(`Uploading ${type} image…`);
    const formData = new FormData();
    formData.append('image', file);
    try {
      const resp = await fetch(`${API_URL}/showcase/my/${type}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (resp.ok) {
        const result = await fetchShowcase();
        if (result) {
          const sc = result.showcase || {};
          setEditBgStyle(sc.bg_style || 'cover');
          setEditBgOverlay(parseFloat(sc.bg_overlay) || 0.70);
        }
      } else {
        const err = await resp.json().catch(() => ({}));
        if (err.code === 'IMAGE_MODERATION_BLOCKED') {
          alert(err.error || 'This image was blocked for inappropriate content and cannot be uploaded.');
        } else {
          alert(err.error || `Failed to upload ${type} image`);
        }
      }
    } catch (err) {
      console.error(`Upload ${type} error:`, err);
    } finally {
      setImageUploading(false);
      setUploadMessage('');
    }
  };

  const deleteImage = async (type) => {
    if (!window.confirm(`Remove ${type} image?`)) return;
    try {
      await fetch(`${API_URL}/showcase/my/${type}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await fetchShowcase();
    } catch (err) {
      console.error(`Delete ${type} error:`, err);
    }
  };

  // ── WYSIWYG: Gallery handlers ───────────────────────────────────────
  const uploadGalleryItem = async (file) => {
    setImageUploading(true);
    setUploadMessage('Uploading photo…');
    const formData = new FormData();
    formData.append('image', file);
    formData.append('category', 'other');
    try {
      const resp = await fetch(`${API_URL}/showcase/my/gallery`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (resp.ok) {
        const item = await resp.json();
        setData(prev => prev ? { ...prev, gallery: [...prev.gallery, item] } : prev);
        setEditingGalleryItem(item);
        setGalleryEditForm({ title: '', caption: '', category: 'other' });
      } else {
        const err = await resp.json().catch(() => ({}));
        if (err.code === 'IMAGE_MODERATION_BLOCKED') {
          alert(err.error || 'This image was blocked for inappropriate content and cannot be uploaded.');
        } else {
          alert(err.error || 'Failed to upload');
        }
      }
    } catch (err) {
      console.error('Gallery upload error:', err);
    } finally {
      setImageUploading(false);
      setUploadMessage('');
    }
  };

  const addYouTubeToGallery = async () => {
    if (!youtubeUrl.trim()) return;
    const videoId = extractYouTubeId(youtubeUrl.trim());
    if (!videoId) { alert('Invalid YouTube URL.'); return; }
    try {
      const resp = await fetch(`${API_URL}/showcase/my/gallery/youtube`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ video_url: youtubeUrl.trim() })
      });
      if (resp.ok) {
        const item = await resp.json();
        setData(prev => prev ? { ...prev, gallery: [...prev.gallery, item] } : prev);
        setYoutubeUrl('');
        setYoutubeInputOpen(false);
        setEditingGalleryItem(item);
        setGalleryEditForm({ title: '', caption: '', category: 'other' });
      } else {
        const err = await resp.json().catch(() => ({}));
        if (err.code === 'SHOWCASE_TIER_LIMIT') alert('Gallery requires Pro or Business.');
        else alert(err.error || 'Failed to add video');
      }
    } catch (err) { console.error('YouTube gallery add error:', err); }
  };

  const updateGalleryItem = async () => {
    if (!editingGalleryItem) return;
    try {
      const resp = await fetch(`${API_URL}/showcase/my/gallery/${editingGalleryItem.gallery_item_id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(galleryEditForm)
      });
      if (resp.ok) {
        const updated = await resp.json();
        setData(prev => prev ? {
          ...prev,
          gallery: prev.gallery.map(g => g.gallery_item_id === updated.gallery_item_id ? updated : g)
        } : prev);
        setEditingGalleryItem(null);
      }
    } catch (err) {
      console.error('Gallery update error:', err);
    }
  };

  const deleteGalleryItem = async (id) => {
    if (!window.confirm('Delete this gallery item?')) return;
    try {
      await fetch(`${API_URL}/showcase/my/gallery/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setData(prev => prev ? {
        ...prev,
        gallery: prev.gallery.filter(g => g.gallery_item_id !== id)
      } : prev);
      // Also remove from card layout if it was promoted
      if (cardLayout) {
        const cleaned = cardLayout.filter(e => !(e.type === 'photo' && e.id === id));
        if (cleaned.length !== cardLayout.length) {
          setCardLayout(cleaned);
          saveCardLayout(cleaned);
        }
      }
      setEditingGalleryItem(null);
    } catch (err) {
      console.error('Gallery delete error:', err);
    }
  };

  // ── Helper: mark dirty on field change ──────────────────────────────
  const changeField = (setter) => (val) => { setter(val); setDirty(true); };

  // ── Sharing helpers ────────────────────────────────────────────────
  const toggleSharing = async (enable) => {
    setShareLoading(true);
    try {
      const endpoint = enable ? 'enable' : 'disable';
      const resp = await fetch(`${API_URL}/collections/share/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (resp.ok) {
        const data = await resp.json();
        setShareSettings(data);
        setShareCopied(false);
      }
    } catch (e) {
      console.error('Error toggling share:', e);
    } finally {
      setShareLoading(false);
    }
  };

  const getFullShareUrl = () => {
    if (!shareSettings?.share_url) return '';
    return `${window.location.origin}${shareSettings.share_url}`;
  };

  const copyShareLink = () => {
    const url = getFullShareUrl();
    if (url) {
      navigator.clipboard.writeText(url).then(() => {
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 3000);
      });
    }
  };

  // ── Card layout helpers ────────────────────────────────────────────
  const saveCardLayout = (layout) => {
    fetch(`${API_URL}/showcase/my/card-layout`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ layout })
    }).catch(err => console.error('Card layout save error:', err));
  };

  const addPhotoToCards = (galleryItem) => {
    const base = cardLayout || (data ? data.items.map(i => ({ type: 'amp', id: i.collection_id })) : []);
    const newLayout = [...base, { type: 'photo', id: galleryItem.gallery_item_id, colSpan: 1, rowSpan: 1 }];
    setCardLayout(newLayout);
    saveCardLayout(newLayout);
  };

  const resizePhotoCard = (galleryItemId, colSpan, rowSpan) => {
    const layout = cardLayout || (data ? data.items.map(i => ({ type: 'amp', id: i.collection_id })) : []);
    const newLayout = layout.map(e =>
      e.type === 'photo' && e.id === galleryItemId
        ? { ...e, colSpan, rowSpan }
        : e
    );
    setCardLayout(newLayout);
    saveCardLayout(newLayout);
    setResizingCardId(null);
  };

  const removePhotoFromCards = (galleryItemId) => {
    const newLayout = (cardLayout || []).filter(e => !(e.type === 'photo' && e.id === galleryItemId));
    setCardLayout(newLayout);
    saveCardLayout(newLayout);
  };

  // ── Drag and Drop handlers ─────────────────────────────────────────
  const dragEndCleanup = useRef(null);

  // Cleanup document listener on unmount
  useEffect(() => () => { if (dragEndCleanup.current) dragEndCleanup.current(); }, []);

  // Identity match helper
  const sameEntry = (a, b) => a && b && a.type === b.type && a.id === b.id;

  // Check if a rendered card is the one being dragged (for dnd-dragging class)
  const isDraggingCard = (card) => {
    if (!dragType) return false;
    const dc = dragCardIdRef.current;
    if (!dc) return false;
    if (card.type === 'amp') return dc.type === 'amp' && dc.id === card.data.collection_id;
    if (card.type === 'photo') return dc.type === 'photo' && dc.id === card.data.gallery_item_id;
    return false;
  };

  // Build a full layout that matches the rendered `cards` order,
  // removing stale entries and including any new amps
  const buildFullLayout = () => {
    let layout = cardLayout || (data ? data.items.map(i => ({ type: 'amp', id: i.collection_id })) : []);
    if (data) {
      // Remove stale entries that no longer exist in data
      const validAmpIds = new Set(data.items.map(i => i.collection_id));
      const validPhotoIds = new Set((data.gallery || []).map(g => g.gallery_item_id));
      layout = layout.filter(entry => {
        if (entry.type === 'amp') return validAmpIds.has(entry.id);
        if (entry.type === 'photo') return validPhotoIds.has(entry.id);
        return false;
      });
      // Add missing amps
      const layoutIds = new Set(layout.filter(el => el.type === 'amp').map(el => el.id));
      const missing = data.items.filter(i => !layoutIds.has(i.collection_id));
      if (missing.length > 0) {
        layout = [...layout, ...missing.map(i => ({ type: 'amp', id: i.collection_id }))];
      }
    }
    return layout;
  };

  // Save if layout changed from pre-drag snapshot
  const saveIfChanged = () => {
    const current = cardLayoutRef.current;
    const changed = preDragData.current &&
      JSON.stringify(current) !== JSON.stringify(preDragData.current);
    if (changed) saveCardLayout(current);
  };

  // Clear all drag state
  const clearDragState = () => {
    dragTypeRef.current = null;
    dragCardIdRef.current = null;
    setDragType(null);
    preDragData.current = null;
  };

  // ── Card drag (reorder within the grid) ─────────────────────────────
  // Uses identity-based splice: finds source & target by {type, id}
  // rather than array index, eliminating index mismatch bugs.
  const handleCardDragStart = (e, cardType, cardId) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', ''); // required for Firefox

    const currentLayout = buildFullLayout();
    setCardLayout(currentLayout);
    cardLayoutRef.current = currentLayout;
    preDragData.current = [...currentLayout];
    dragCardIdRef.current = { type: cardType, id: cardId };
    dragTypeRef.current = 'cards';

    // Delay applying visual drag class so browser captures a full ghost image
    requestAnimationFrame(() => {
      if (dragTypeRef.current !== 'cards') return;
      setDragType('cards');
    });

    // Document-level dragend for reliable save — React DOM mutations
    // during drag can cause per-element onDragEnd to not fire
    if (dragEndCleanup.current) dragEndCleanup.current();
    const onDocDragEnd = () => {
      document.removeEventListener('dragend', onDocDragEnd, true);
      dragEndCleanup.current = null;
      saveIfChanged();
      clearDragState();
    };
    dragEndCleanup.current = () => document.removeEventListener('dragend', onDocDragEnd, true);
    document.addEventListener('dragend', onDocDragEnd, true);
  };

  const handleCardDragEnter = (e, cardType, cardId) => {
    e.preventDefault();  // required: marks this element as a valid drop target
    const curType = dragTypeRef.current;

    // Gallery-to-cards: first entry into card grid (insert new photo)
    if (curType === 'gallery-to-cards' && !dragCardIdRef.current && galleryDragItem.current) {
      const entry = { type: 'photo', id: galleryDragItem.current.gallery_item_id, colSpan: 1, rowSpan: 1 };
      const arr = [...(cardLayoutRef.current || [])];
      const tgtIdx = arr.findIndex(el => el.type === cardType && el.id === cardId);
      arr.splice(tgtIdx >= 0 ? tgtIdx : arr.length, 0, entry);
      setCardLayout(arr);
      cardLayoutRef.current = arr;
      dragCardIdRef.current = entry; // now tracking the inserted photo
      return;
    }

    // Live reorder via identity-based splice
    if (dragCardIdRef.current && (curType === 'cards' || curType === 'gallery-to-cards')) {
      const target = { type: cardType, id: cardId };
      if (sameEntry(dragCardIdRef.current, target)) return; // hovering over self

      const arr = [...(cardLayoutRef.current || [])];
      const srcIdx = arr.findIndex(el => sameEntry(el, dragCardIdRef.current));
      const tgtIdx = arr.findIndex(el => sameEntry(el, target));
      if (srcIdx === -1 || tgtIdx === -1 || srcIdx === tgtIdx) return;

      const [moved] = arr.splice(srcIdx, 1);
      arr.splice(tgtIdx, 0, moved);
      setCardLayout(arr);
      cardLayoutRef.current = arr;
    }
  };

  // Per-element dragEnd fallback (document listener is primary)
  const handleCardDragEnd = () => {
    if (dragEndCleanup.current) {
      dragEndCleanup.current();
      dragEndCleanup.current = null;
    }
    saveIfChanged();
    clearDragState();
  };

  const handleGridDrop = (e) => {
    e.preventDefault();
    // Gallery-to-cards: if photo hasn't been inserted yet (dropped on grid gaps), append it
    if (dragTypeRef.current === 'gallery-to-cards' && !dragCardIdRef.current && galleryDragItem.current) {
      const entry = { type: 'photo', id: galleryDragItem.current.gallery_item_id, colSpan: 1, rowSpan: 1 };
      const arr = [...(cardLayoutRef.current || []), entry];
      setCardLayout(arr);
      cardLayoutRef.current = arr;
      dragCardIdRef.current = entry;
    }
  };

  // ── Gallery-to-cards drag ───────────────────────────────────────────
  const handleGalleryPhotoStart = (e, item) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', ''); // required for Firefox
    const currentLayout = buildFullLayout();
    setCardLayout(currentLayout);
    cardLayoutRef.current = currentLayout;
    preDragData.current = [...currentLayout];
    galleryDragItem.current = item;
    dragCardIdRef.current = null; // not yet inserted into card grid
    dragTypeRef.current = 'gallery-to-cards';
    setGalleryDragId(item.gallery_item_id);

    requestAnimationFrame(() => {
      if (dragTypeRef.current !== 'gallery-to-cards') return;
      setDragType('gallery-to-cards');
    });

    // Document-level dragend
    if (dragEndCleanup.current) dragEndCleanup.current();
    const onDocDragEnd = () => {
      document.removeEventListener('dragend', onDocDragEnd, true);
      dragEndCleanup.current = null;
      saveIfChanged();
      clearDragState();
      setGalleryDragId(null);
      galleryDragItem.current = null;
    };
    dragEndCleanup.current = () => document.removeEventListener('dragend', onDocDragEnd, true);
    document.addEventListener('dragend', onDocDragEnd, true);
  };

  const handleGalleryPhotoEnd = () => {
    if (dragEndCleanup.current) {
      dragEndCleanup.current();
      dragEndCleanup.current = null;
    }
    saveIfChanged();
    clearDragState();
    setGalleryDragId(null);
    galleryDragItem.current = null;
  };

  // ── File drop onto gallery (upload from desktop) ─────────────────────
  const isFileDrag = (e) => e.dataTransfer.types && e.dataTransfer.types.indexOf('Files') !== -1;

  const handleFileDragEnter = (e) => {
    if (!isFileDrag(e)) return;       // let internal gallery/card drags pass through
    e.preventDefault();
    e.stopPropagation();
    fileDragCounter.current++;
    setFileDragOver(true);
  };

  const handleFileDragLeave = (e) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    fileDragCounter.current--;
    if (fileDragCounter.current <= 0) {
      fileDragCounter.current = 0;
      setFileDragOver(false);
    }
  };

  const handleFileDragOver = (e) => {
    if (!isFileDrag(e)) return;       // don't make gallery section a drop target for internal drags
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleFileDrop = async (e) => {
    if (!isFileDrag(e)) return;       // not a file drop — let it bubble
    e.preventDefault();
    e.stopPropagation();
    setFileDragOver(false);
    fileDragCounter.current = 0;

    // Only handle actual file drops from desktop, not gallery-item reorder drags
    if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) return;
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length === 0) return;

    setFileUploading(true);
    setImageUploading(true);
    setUploadProgress({ current: 0, total: files.length });
    setUploadMessage('Uploading photos…');
    let lastItem = null;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress({ current: i + 1, total: files.length });
      const formData = new FormData();
      formData.append('image', file);
      formData.append('category', 'other');
      try {
        const resp = await fetch(`${API_URL}/showcase/my/gallery`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        });
        if (resp.ok) {
          const item = await resp.json();
          setData(prev => prev ? { ...prev, gallery: [...prev.gallery, item] } : prev);
          lastItem = item;
        } else {
          const err = await resp.json().catch(() => ({}));
          if (err.code === 'IMAGE_MODERATION_BLOCKED') {
            alert(`${file.name} was blocked for inappropriate content and cannot be uploaded.`);
          }
        }
      } catch (err) {
        console.error('Gallery file drop upload error:', err);
      }
    }
    setFileUploading(false);
    setImageUploading(false);
    setUploadMessage('');
    setUploadProgress(null);
    if (lastItem) {
      setEditingGalleryItem(lastItem);
      setGalleryEditForm({ title: '', caption: '', category: 'other' });
    }
  };

  // ── Computed: unified card grid + gallery-only pool ──────────────────
  const cards = useMemo(() => {
    if (!data) return [];
    const { items: allItems = [], gallery: allGallery = [] } = data;
    if (!cardLayout || cardLayout.length === 0) {
      return allItems.map(item => ({ type: 'amp', data: item }));
    }
    const result = [];
    const usedAmpIds = new Set();
    for (const entry of cardLayout) {
      if (entry.type === 'amp') {
        const amp = allItems.find(i => i.collection_id === entry.id);
        if (amp) { result.push({ type: 'amp', data: amp }); usedAmpIds.add(entry.id); }
      } else if (entry.type === 'photo') {
        const photo = allGallery.find(g => g.gallery_item_id === entry.id);
        if (photo) { result.push({ type: 'photo', data: photo, colSpan: entry.colSpan || 1, rowSpan: entry.rowSpan || 1 }); }
      }
    }
    for (const item of allItems) {
      if (!usedAmpIds.has(item.collection_id)) {
        result.push({ type: 'amp', data: item });
      }
    }
    return result;
  }, [data, cardLayout]);

  const galleryOnly = useMemo(() => {
    if (!data) return [];
    const photoIdsInCards = new Set(
      (cardLayout || []).filter(e => e.type === 'photo').map(e => e.id)
    );
    return (data.gallery || []).filter(g => {
      // During gallery-to-cards drag, keep the dragged photo's DOM element alive
      // in the gallery so the browser's dragend event can bubble to the document.
      // Without this, removing the drag-source element mid-drag prevents dragend
      // from firing, leaving the card stuck with visibility:hidden.
      if (galleryDragId === g.gallery_item_id) return true;
      return !photoIdsInCards.has(g.gallery_item_id);
    });
  }, [data, cardLayout, galleryDragId]);

  // ── Loading ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="shared-collection-screen">
        <div className="shared-loading">
          <div className="shared-loading-spinner" />
          <p>Loading collection…</p>
        </div>
      </div>
    );
  }

  // ── Error / Not Found ────────────────────────────────────────────────
  if (error) {
    return (
      <div className="shared-collection-screen">
        <div className="shared-collection-content">
          <div className="shared-collection-container">
            <div className="shared-not-found">
              {error === 'not_found' ? (
                <><h2>🔌 Collection Not Found</h2><p>This collection link may have been disabled or doesn't exist.</p></>
              ) : (
                <><h2>⚡ Something went wrong</h2><p>We couldn't load this collection right now. Please try again later.</p></>
              )}
              <Link to="/" className="shared-cta-btn">Explore AmpNebula</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Destructure data ─────────────────────────────────────────────────
  const { owner, showcase, gallery, stats } = data;
  const hasBg = showcase?.has_bg_image;
  const hasHero = showcase?.has_hero_image;
  const activeBgStyle = editMode ? editBgStyle : (showcase?.bg_style || 'cover');
  const activeBgOverlay = editMode ? editBgOverlay : (showcase?.bg_overlay || 0.70);
  const activeTagline = editMode ? editTagline : (showcase?.tagline || '');
  const activeBio = editMode ? editBio : (showcase?.bio || '');
  const activeShowStats = editMode ? editShowStats : (showcase?.show_stats !== false);
  const activeShowGallery = editMode ? editShowGallery : (showcase?.show_gallery !== false);
  const activeShowAmps = editMode ? editShowAmps : (showcase?.show_amps !== false);
  const sections = (showcase?.sections_order || 'hero,stats,amps,gallery').split(',').map(s => s.trim());

  const yearDisplay = (item) => {
    if (item.start_year && item.end_year && item.start_year !== item.end_year) return `${item.start_year}–${item.end_year}`;
    if (item.start_year) return `${item.start_year}`;
    return null;
  };
  const ampDisplayName = (item) => item.parent_name || item.amplifier_name || item.model || 'Unknown Amp';

  // ═══════════════════════════════════════════════════════════════════════
  //  SECTION RENDERERS (with inline edit overlays when editMode)
  // ═══════════════════════════════════════════════════════════════════════

  const renderHero = () => {
    if (hasHero) {
      return (
        <div className={`shared-hero ${editMode ? 'editable' : ''}`} key="hero">
          <img src={`${API_URL}/showcase/${owner.user_id}/hero.jpg?t=${Date.now()}`} alt="Collection hero" />
          <div className="shared-hero-overlay">
            {editMode && editingField === 'tagline' ? (
              <input
                ref={taglineRef}
                className="wysiwyg-inline-input wysiwyg-tagline-input"
                type="text" maxLength={200}
                value={editTagline}
                placeholder="Add a tagline…"
                onChange={(e) => changeField(setEditTagline)(e.target.value)}
                onBlur={() => setEditingField(null)}
                onKeyDown={(e) => { if (e.key === 'Enter') setEditingField(null); }}
                autoFocus
              />
            ) : (
              <h2
                className={`shared-hero-tagline ${editMode ? 'wysiwyg-clickable' : ''}`}
                onClick={editMode ? () => { setEditingField('tagline'); setTimeout(() => taglineRef.current?.focus(), 50); } : undefined}
              >
                {activeTagline || (editMode ? 'Click to add tagline…' : '')}
              </h2>
            )}
            <span className="shared-hero-owner">{owner.display_name}'s Collection</span>
          </div>
          {editMode && (
            <div className="wysiwyg-image-controls">
              <button className="wysiwyg-img-btn" onClick={() => heroInputRef.current?.click()} title="Change hero image">📷 Change</button>
              <button className="wysiwyg-img-btn danger" onClick={() => deleteImage('hero')} title="Remove hero image">✕ Remove</button>
            </div>
          )}
        </div>
      );
    }

    // No hero image — edit mode shows upload placeholder
    if (editMode) {
      return (
        <div className="shared-hero editable no-hero" key="hero" onClick={() => heroInputRef.current?.click()}>
          <div className="wysiwyg-hero-placeholder">
            <span className="wysiwyg-hero-placeholder-icon">🖼️</span>
            <span>Click to upload a hero banner</span>
            <span className="wysiwyg-hint">Recommended: 1200×400 or wider</span>
          </div>
        </div>
      );
    }

    // Public view — simple header
    return (
      <div className="shared-header" key="hero">
        <div className="shared-owner-section">
          {owner.has_photo ? (
            <img src={`${API_URL}/users/${owner.user_id}/photo`} alt={owner.display_name} className="shared-owner-avatar" />
          ) : (
            <div className="shared-owner-avatar-placeholder">
              {(owner.display_name || 'U').charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <h1 className="shared-title">{owner.display_name}'s Collection</h1>
            <p className="shared-subtitle">Shared via AmpNebula</p>
            {activeTagline && <p className="shared-tagline">{activeTagline}</p>}
          </div>
        </div>
      </div>
    );
  };

  const renderBio = () => {
    if (editMode) {
      return (
        <div className="shared-bio-wrapper editable" key="bio">
          {editingField === 'bio' ? (
            <textarea
              ref={bioRef}
              className="wysiwyg-inline-textarea"
              maxLength={2000}
              value={editBio}
              placeholder="Tell visitors about your gear journey…"
              onChange={(e) => changeField(setEditBio)(e.target.value)}
              onBlur={() => setEditingField(null)}
              autoFocus rows={4}
            />
          ) : (
            <p
              className={`shared-bio wysiwyg-clickable ${!activeBio ? 'wysiwyg-placeholder-text' : ''}`}
              onClick={() => { setEditingField('bio'); setTimeout(() => bioRef.current?.focus(), 50); }}
            >
              {activeBio || 'Click to add your bio…'}
            </p>
          )}
        </div>
      );
    }
    if (!activeBio) return null;
    return <p className="shared-bio" key="bio">{activeBio}</p>;
  };

  const renderStats = () => {
    if (!activeShowStats) {
      if (editMode) {
        return (
          <div className="shared-section wysiwyg-hidden-section" key="stats">
            <div className="shared-section-card wysiwyg-faded">
              <div className="wysiwyg-hidden-label">📊 Stats — Hidden <span>(toggle in ⚙️ Settings)</span></div>
            </div>
          </div>
        );
      }
      return null;
    }
    return (
      <div className="shared-section" key="stats">
        <div className="shared-section-card">
          <div className="shared-stats-row">
            <div className="shared-stat">
              <span className="shared-stat-value">{stats.total_owned}</span>
              <span className="shared-stat-label">{stats.total_owned === 1 ? 'Amplifier' : 'Amplifiers'}</span>
            </div>
            <div className="shared-stat">
              <span className="shared-stat-value">{stats.unique_manufacturers}</span>
              <span className="shared-stat-label">{stats.unique_manufacturers === 1 ? 'Manufacturer' : 'Manufacturers'}</span>
            </div>
            {gallery.length > 0 && (
              <div className="shared-stat">
                <span className="shared-stat-value">{gallery.length}</span>
                <span className="shared-stat-label">Gallery Items</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCards = () => {
    if (!activeShowAmps) {
      if (editMode) {
        return (
          <div className="shared-section wysiwyg-hidden-section" key="amps">
            <div className="shared-section-card wysiwyg-faded">
              <div className="wysiwyg-hidden-label">🔊 Amp Collection — Hidden <span>(toggle in ⚙️ Settings)</span></div>
            </div>
          </div>
        );
      }
      return null;
    }
    if (cards.length === 0) return null;
    return (
      <div className="shared-section" key="amps">
        <div className="shared-section-card">
          <div className="shared-section-title">🔊 Amp Collection</div>
          <div
            className="shared-grid"
            onDragEnter={editMode ? (e) => {
              e.preventDefault();
              // Gallery-to-cards: if entering the grid container directly (not a card),
              // append the photo at the end so it appears in the grid immediately
              if (dragTypeRef.current === 'gallery-to-cards' && !dragCardIdRef.current && galleryDragItem.current) {
                const entry = { type: 'photo', id: galleryDragItem.current.gallery_item_id, colSpan: 1, rowSpan: 1 };
                const arr = [...(cardLayoutRef.current || []), entry];
                setCardLayout(arr);
                cardLayoutRef.current = arr;
                dragCardIdRef.current = entry;
              }
            } : undefined}
            onDragOver={editMode ? (e) => e.preventDefault() : undefined}
            onDrop={editMode ? handleGridDrop : undefined}
          >
            {cards.map((card, index) => {
              if (card.type === 'photo') {
                const photo = card.data;
                const cs = card.colSpan || 1;
                const rs = card.rowSpan || 1;
                const spanStyle = {};
                if (cs > 1) spanStyle.gridColumn = `span ${cs}`;
                if (rs > 1) spanStyle.gridRow = `span ${rs}`;
                return (
                  <div
                    key={`photo-${photo.gallery_item_id}`}
                    className={`shared-photo-card ${editMode ? 'dnd-card' : ''} ${isDraggingCard(card) ? 'dnd-dragging' : ''} ${cs > 1 || rs > 1 ? 'photo-card-spanned' : ''}`}
                    style={spanStyle}
                    draggable={editMode}
                    onDragStart={editMode ? (e) => handleCardDragStart(e, 'photo', photo.gallery_item_id) : undefined}
                    onDragEnter={editMode ? (e) => handleCardDragEnter(e, 'photo', photo.gallery_item_id) : undefined}
                    onDragOver={editMode ? (e) => e.preventDefault() : undefined}
                    onDrop={editMode ? handleGridDrop : undefined}
                    onDragEnd={editMode ? handleCardDragEnd : undefined}
                    onClick={() => setLightboxItem({ ...photo, _type: 'gallery' })}
                  >
                    {editMode && <div className="dnd-handle" title="Drag to reorder">⠿</div>}
                    {editMode && (
                      <button className="photo-card-remove" title="Remove from showcase grid" onClick={(e) => { e.stopPropagation(); removePhotoFromCards(photo.gallery_item_id); }}>×</button>
                    )}
                    {editMode && (
                      <button
                        className="photo-card-resize-btn"
                        title="Resize card"
                        onClick={(e) => { e.stopPropagation(); setResizingCardId(resizingCardId === photo.gallery_item_id ? null : photo.gallery_item_id); }}
                      >
                        ⤡
                      </button>
                    )}
                    {resizingCardId === photo.gallery_item_id && (
                      <div className="photo-card-size-menu" onClick={(e) => e.stopPropagation()}>
                        <div className="photo-card-size-menu-title">Card Size</div>
                        <div className="photo-card-size-options">
                          {[
                            { c: 1, r: 1, label: '1×1' },
                            { c: 2, r: 1, label: '2×1' },
                            { c: 3, r: 1, label: '3×1' },
                            { c: 1, r: 2, label: '1×2' },
                            { c: 2, r: 2, label: '2×2' },
                          ].map(opt => (
                            <button
                              key={opt.label}
                              className={`photo-card-size-opt ${cs === opt.c && rs === opt.r ? 'active' : ''}`}
                              onClick={() => resizePhotoCard(photo.gallery_item_id, opt.c, opt.r)}
                            >
                              <span className="photo-card-size-grid" data-cols={opt.c} data-rows={opt.r}>
                                {Array.from({ length: opt.c * opt.r }).map((_, i) => <span key={i} className="photo-card-size-cell" />)}
                              </span>
                              <span className="photo-card-size-label">{opt.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {photo.item_type === 'youtube' ? (
                      <div style={{ position: 'relative', height: '100%' }}>
                        <img src={`https://img.youtube.com/vi/${extractYouTubeId(photo.video_url)}/hqdefault.jpg`}
                          alt={photo.title || 'YouTube'} className="shared-photo-card-img" draggable="false" />
                        <div className="shared-gallery-youtube-play-overlay">▶️</div>
                      </div>
                    ) : (
                      <img src={`${API_URL}/showcase/gallery/${photo.gallery_item_id}/image`} alt={photo.title || 'Gallery photo'} className="shared-photo-card-img" draggable="false" />
                    )}
                    {(photo.title || photo.caption) && (
                      <div className="shared-photo-card-info">
                        {photo.title && <div className="shared-photo-card-title">{photo.title}</div>}
                        {photo.caption && <div className="shared-photo-card-caption">{photo.caption}</div>}
                      </div>
                    )}
                  </div>
                );
              }
              const item = card.data;
              const year = yearDisplay(item);
              const name = ampDisplayName(item);
              const parentSlug = item.parent_slug || item.amplifier_slug;
              const mfrSlug = item.manufacturer_slug;
              const parentLink = parentSlug && mfrSlug ? `/amps/${mfrSlug}/${parentSlug}` : null;
              return (
                <div
                  key={`amp-${item.collection_id}`}
                  className={`shared-amp-card ${editMode ? 'dnd-card' : ''} ${isDraggingCard(card) ? 'dnd-dragging' : ''}`}
                  draggable={editMode}
                  onDragStart={editMode ? (e) => handleCardDragStart(e, 'amp', item.collection_id) : undefined}
                  onDragEnter={editMode ? (e) => handleCardDragEnter(e, 'amp', item.collection_id) : undefined}
                  onDragOver={editMode ? (e) => e.preventDefault() : undefined}
                  onDrop={editMode ? handleGridDrop : undefined}
                  onDragEnd={editMode ? handleCardDragEnd : undefined}
                >
                  {editMode && <div className="dnd-handle" title="Drag to reorder">⠿</div>}
                  {item.thumbnail_artifact_id ? (
                    <img
                      src={`${API_URL}/amplifier_artifacts/${item.thumbnail_artifact_id}`}
                      alt={name}
                      className="shared-amp-thumb"
                      loading="lazy"
                      draggable="false"
                      onClick={(e) => { e.stopPropagation(); setLightboxItem({ _type: 'artifact', artifact_id: item.thumbnail_artifact_id, title: `${item.manufacturer_name} ${name}` }); }}
                      style={{ cursor: 'pointer' }}
                    />
                  ) : (
                    <div className="shared-amp-thumb-placeholder">🔊</div>
                  )}
                  <div className="shared-amp-info">
                    <div className="shared-amp-manufacturer">{item.manufacturer_name}</div>
                    <h3 className="shared-amp-name">{name}</h3>
                    {parentLink && !editMode && (
                      <div style={{ fontSize: '0.75rem', marginTop: '2px' }}>
                        <Link to={parentLink} style={{ color: '#718096', textDecoration: 'none' }}>
                          View specs →
                        </Link>
                      </div>
                    )}
                    <div className="shared-amp-meta">
                      {year && <span className="shared-amp-badge year">📅 {year}</span>}
                      {item.condition && <span className="shared-amp-badge condition">✓ {item.condition}</span>}
                      {parseInt(item.photo_count) > 0 && <span className="shared-amp-badge photos">📷 {item.photo_count}</span>}
                      {item.acquisition_type && item.acquisition_type !== 'Purchased' && (
                        <span className="shared-amp-badge">{item.acquisition_type}</span>
                      )}
                    </div>
                    {item.notes && <div className="shared-amp-notes">{item.notes}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderGallery = () => {
    if (!activeShowGallery && !editMode) return null;
    if (!activeShowGallery && editMode) {
      return (
        <div className="shared-section wysiwyg-hidden-section" key="gallery">
          <div className="shared-section-card wysiwyg-faded">
            <div className="wysiwyg-hidden-label">📸 Gear Gallery — Hidden <span>(toggle in ⚙️ Settings)</span></div>
          </div>
        </div>
      );
    }
    if (galleryOnly.length === 0 && !editMode) return null;

    return (
      <div
        className={`shared-section ${fileDragOver ? 'gallery-file-drag-over' : ''}`}
        key="gallery"
        onDragEnter={editMode ? handleFileDragEnter : undefined}
        onDragLeave={editMode ? handleFileDragLeave : undefined}
        onDragOver={editMode ? handleFileDragOver : undefined}
        onDrop={editMode ? handleFileDrop : undefined}
      >
        {/* File drop overlay */}
        {editMode && (
          <div className={`gallery-drop-overlay ${fileDragOver ? 'visible' : ''}`}>
            <div className="gallery-drop-text">
              {fileUploading ? '⏳ Uploading...' : '📸 Drop images here to add to gallery'}
            </div>
          </div>
        )}
        <div className="shared-section-card">
          <div className="shared-section-title">📸 Gear Gallery</div>
          {editMode && galleryOnly.length > 0 && (
            <div className="gallery-drag-hint">💡 Drag a photo or video up to the amp grid, or click ➕ to promote it</div>
          )}
          <div className="shared-gallery-grid">
            {galleryOnly.map((item) => (
              <div
                key={item.gallery_item_id}
                className={`shared-gallery-item ${editMode ? 'editable' : ''} ${galleryDragId === item.gallery_item_id ? 'gallery-dragging' : ''}`}
                draggable={editMode}
                onDragStart={editMode ? (e) => handleGalleryPhotoStart(e, item) : undefined}
                onDragEnd={editMode ? handleGalleryPhotoEnd : undefined}
                onClick={editMode ? () => {
                  setEditingGalleryItem(item);
                  setGalleryEditForm({ title: item.title || '', caption: item.caption || '', category: item.category || 'other' });
                } : () => setLightboxItem({ ...item, _type: 'gallery' })}
              >
                {item.item_type === 'youtube' ? (
                  <>
                    <img src={`https://img.youtube.com/vi/${extractYouTubeId(item.video_url)}/hqdefault.jpg`}
                      alt={item.title || 'YouTube'} loading="lazy" draggable="false" />
                    <div className="shared-gallery-youtube-play">▶️</div>
                  </>
                ) : (
                  <img src={`${API_URL}/showcase/gallery/${item.gallery_item_id}/image`} alt={item.title || 'Gallery item'} loading="lazy" draggable="false" />
                )}
                {(item.title || item.category !== 'other') && (
                  <div className="shared-gallery-item-overlay">
                    {item.title && <div className="shared-gallery-item-title">{item.title}</div>}
                    {item.category !== 'other' && <div className="shared-gallery-item-category">{item.category}</div>}
                  </div>
                )}
                {editMode && <div className="wysiwyg-gallery-edit-badge">✏️</div>}
                {editMode && (
                  <button
                    className="gallery-add-to-cards"
                    title="Add to amp collection grid"
                    onClick={(e) => { e.stopPropagation(); addPhotoToCards(item); }}
                  >
                    ➕
                  </button>
                )}
              </div>
            ))}
            {editMode && (
              <>
                <div className="shared-gallery-item wysiwyg-gallery-add" onClick={() => galleryInputRef.current?.click()}>
                  <div className="wysiwyg-gallery-add-inner">
                    <span className="wysiwyg-gallery-add-icon">+</span>
                    <span>Add Photo</span>
                  </div>
                </div>
                {youtubeInputOpen ? (
                  <div className="shared-gallery-item wysiwyg-gallery-youtube-input" onClick={e => e.stopPropagation()}>
                    <input type="url" placeholder="YouTube URL…" value={youtubeUrl}
                      onChange={e => setYoutubeUrl(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addYouTubeToGallery()}
                      autoFocus />
                    <div>
                      <button onClick={addYouTubeToGallery}>Add</button>
                      <button onClick={() => { setYoutubeInputOpen(false); setYoutubeUrl(''); }}>✕</button>
                    </div>
                  </div>
                ) : (
                  <div className="shared-gallery-item wysiwyg-gallery-add" onClick={() => setYoutubeInputOpen(true)}>
                    <div className="wysiwyg-gallery-add-inner">
                      <span className="wysiwyg-gallery-add-icon">▶</span>
                      <span>Add Video</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const sectionRenderers = { hero: renderHero, stats: renderStats, amps: renderCards, gallery: renderGallery };

  // ═══════════════════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════════════════
  return (
    <div className={`shared-collection-screen ${hasBg ? 'has-bg' : ''} ${editMode ? 'wysiwyg-active' : ''}`}>
      <UploadOverlay
        visible={imageUploading}
        message={uploadMessage}
        subtext="Scanning for content safety…"
        progress={uploadProgress}
      />
      {/* Background layer */}
      {hasBg && (
        <>
          <div
            className={`shared-bg-layer ${activeBgStyle === 'tile' ? 'bg-tile' : ''} ${activeBgStyle === 'fixed' ? 'bg-fixed' : ''}`}
            style={{ backgroundImage: `url(${API_URL}/showcase/${owner.user_id}/bg.jpg?t=${Date.now()})` }}
          />
          <div className="shared-bg-overlay" style={{ opacity: activeBgOverlay }} />
        </>
      )}

      {/* ── Owner Preview Banner (public view, owner is logged in) ─── */}
      {!editMode && isOwner && (
        <div className="owner-preview-banner">
          <div className="owner-preview-inner">
            <span className="owner-preview-label">👁️ You're viewing your showcase as the public sees it</span>
          </div>
        </div>
      )}

      {/* ── WYSIWYG Edit Toolbar ──────────────────────────────────── */}
      {editMode && (
        <div className="wysiwyg-toolbar">
          <div className="wysiwyg-toolbar-inner">
            <div className="wysiwyg-toolbar-left">
              <Link to="/my-collection" className="wysiwyg-btn-back" title="Back to My Collection">
                ← Back
              </Link>
              <span className="wysiwyg-toolbar-label">✏️ Editing Your Showcase</span>
            </div>
            <div className="wysiwyg-toolbar-right">
              {saveFlash === 'saved' && <span className="wysiwyg-save-flash">✓ Published</span>}
              {saveFlash === 'error' && <span className="wysiwyg-save-flash error">Save failed</span>}
              <button
                className="wysiwyg-btn wysiwyg-btn-preview"
                onClick={() => window.open(`/collection/${shareToken}`, '_blank')}
                title="Preview what the public sees (opens new tab)"
              >
                👁️ Preview
              </button>
              <button
                className={`wysiwyg-btn wysiwyg-btn-share ${shareSettings?.share_enabled ? 'live' : ''}`}
                onClick={() => { setShareOpen(!shareOpen); setSettingsOpen(false); }}
                title="Sharing settings"
              >
                {shareSettings?.share_enabled ? '🌐 Public' : '🔒 Draft'}
              </button>
              <button className="wysiwyg-btn wysiwyg-btn-settings" onClick={() => { setSettingsOpen(!settingsOpen); setShareOpen(false); }} title="Page settings">
                ⚙️ Settings
              </button>
              <button
                className={`wysiwyg-btn wysiwyg-btn-publish ${dirty ? 'has-changes' : ''}`}
                onClick={publishChanges} disabled={saving || !dirty}
              >
                {saving ? 'Publishing…' : dirty ? '● Publish Changes' : 'Published'}
              </button>
              <Link to="/my-collection" className="wysiwyg-btn wysiwyg-btn-exit" title="Back to My Collection">
                ✕
              </Link>
            </div>
          </div>

          {/* ── Share Panel ─────────────────────────────────────────── */}
          {shareOpen && (
            <div className="wysiwyg-share-panel">
              <div className="wysiwyg-share-panel-inner">
                <div className="wysiwyg-share-row">
                  <span className="wysiwyg-share-label">Public sharing</span>
                  <label className="wysiwyg-share-switch">
                    <input
                      type="checkbox"
                      checked={shareSettings?.share_enabled || false}
                      onChange={(e) => toggleSharing(e.target.checked)}
                      disabled={shareLoading}
                    />
                    <span className="wysiwyg-share-slider" />
                  </label>
                </div>
                {shareSettings?.share_enabled ? (
                  <p className="wysiwyg-share-status">🌐 Anyone with the link can view your showcase</p>
                ) : (
                  <p className="wysiwyg-share-status">🔒 Only you can see this page right now</p>
                )}
                {shareSettings?.share_url && (
                  <div className="wysiwyg-share-link-row">
                    <input
                      type="text"
                      readOnly
                      value={getFullShareUrl()}
                      className="wysiwyg-share-link-input"
                      onClick={(e) => e.target.select()}
                    />
                    <button
                      className={`wysiwyg-btn wysiwyg-share-copy-btn ${shareCopied ? 'copied' : ''}`}
                      onClick={copyShareLink}
                    >
                      {shareCopied ? '✓ Copied' : '📋 Copy'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Settings Drawer ─────────────────────────────────────── */}
          {settingsOpen && (
            <div className="wysiwyg-settings-drawer">
              <div className="wysiwyg-settings-section">
                <div className="wysiwyg-settings-title">Custom URL</div>
                <div className="wysiwyg-slug-row">
                  <span className="wysiwyg-slug-prefix">ampnebula.com/collection/</span>
                  <input
                    className="wysiwyg-slug-input"
                    type="text" maxLength={60} placeholder="my-collection"
                    value={editSlug}
                    onChange={(e) => {
                      const val = e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                      changeField(setEditSlug)(val);
                    }}
                  />
                </div>
              </div>

              <div className="wysiwyg-settings-section">
                <div className="wysiwyg-settings-title">Background Image</div>
                {hasBg ? (
                  <div className="wysiwyg-bg-controls">
                    <div className="wysiwyg-bg-preview">
                      <img src={`${API_URL}/showcase/${owner.user_id}/bg.jpg?t=${Date.now()}`} alt="Background" />
                      <div className="wysiwyg-bg-actions">
                        <button className="wysiwyg-img-btn small" onClick={() => bgInputRef.current?.click()}>📷 Change</button>
                        <button className="wysiwyg-img-btn small danger" onClick={() => deleteImage('background')}>✕ Remove</button>
                      </div>
                    </div>
                    <div className="wysiwyg-bg-options">
                      {['cover', 'tile', 'fixed'].map(s => (
                        <button key={s} className={`wysiwyg-bg-option ${editBgStyle === s ? 'active' : ''}`}
                          onClick={() => changeField(setEditBgStyle)(s)}>
                          {s === 'cover' ? '📐 Cover' : s === 'tile' ? '🔲 Tile' : '📌 Fixed'}
                        </button>
                      ))}
                    </div>
                    <div className="wysiwyg-overlay-row">
                      <span>Dark overlay</span>
                      <input type="range" min="0" max="1" step="0.05" value={editBgOverlay}
                        onChange={(e) => changeField(setEditBgOverlay)(parseFloat(e.target.value))} />
                      <span>{Math.round(editBgOverlay * 100)}%</span>
                    </div>
                  </div>
                ) : (
                  <button className="wysiwyg-img-btn" onClick={() => bgInputRef.current?.click()}>
                    🎨 Upload Background
                  </button>
                )}
              </div>

              <div className="wysiwyg-settings-section">
                <div className="wysiwyg-settings-title">Visible Sections</div>
                <label className="wysiwyg-toggle">
                  <input type="checkbox" checked={editShowStats} onChange={(e) => changeField(setEditShowStats)(e.target.checked)} />
                  Show collection stats
                </label>
                <label className="wysiwyg-toggle">
                  <input type="checkbox" checked={editShowAmps} onChange={(e) => changeField(setEditShowAmps)(e.target.checked)} />
                  Show amplifier list
                </label>
                <label className="wysiwyg-toggle">
                  <input type="checkbox" checked={editShowGallery} onChange={(e) => changeField(setEditShowGallery)(e.target.checked)} />
                  Show gear gallery
                </label>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Content ──────────────────────────────────────────────────── */}
      <div className={`shared-collection-content ${editMode ? 'has-toolbar' : ''}`}>
        <div className="shared-collection-container">
          {sections.map(section => {
            const renderer = sectionRenderers[section];
            return renderer ? renderer() : null;
          })}
          {renderBio()}
          {['stats', 'amps', 'gallery'].filter(s => !sections.includes(s)).map(section => {
            const renderer = sectionRenderers[section];
            return renderer ? renderer() : null;
          })}
          {!editMode && (
            <div className="shared-branding">
              <p>🎛️ Powered by <a href="/">AmpNebula</a> — The world's most comprehensive amplifier database</p>
              <p style={{ marginTop: '0.5rem' }}>
                <Link to="/signup" className="shared-cta-btn">Start Your Free Collection</Link>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Hidden file inputs */}
      <input ref={heroInputRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={(e) => { if (e.target.files[0]) uploadImage('hero', e.target.files[0]); e.target.value = ''; }} />
      <input ref={bgInputRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={(e) => { if (e.target.files[0]) uploadImage('background', e.target.files[0]); e.target.value = ''; }} />
      <input ref={galleryInputRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={(e) => { if (e.target.files[0]) uploadGalleryItem(e.target.files[0]); e.target.value = ''; }} />

      {/* Gallery / Artifact Lightbox */}
      {lightboxItem && (
        <div className="shared-gallery-lightbox" onClick={() => setLightboxItem(null)}>
          <button className="shared-gallery-lightbox-close" onClick={() => setLightboxItem(null)}>✕</button>
          {lightboxItem._type === 'gallery' && lightboxItem.item_type === 'youtube' ? (
            <div className="shared-gallery-lightbox-youtube" onClick={e => e.stopPropagation()}>
              <iframe
                src={`https://www.youtube.com/embed/${extractYouTubeId(lightboxItem.video_url)}`}
                title={lightboxItem.title || 'YouTube video'}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ width: '100%', maxWidth: '900px', aspectRatio: '16/9', border: 'none' }}
              />
            </div>
          ) : (
            <img
              src={lightboxItem._type === 'artifact'
                ? `${API_URL}/amplifier_artifacts/${lightboxItem.artifact_id}`
                : `${API_URL}/showcase/gallery/${lightboxItem.gallery_item_id}/image`}
              alt={lightboxItem.title || 'Gallery item'}
              onClick={e => e.stopPropagation()}
            />
          )}
          {(lightboxItem.title || lightboxItem.caption) && (
            <div className="shared-gallery-lightbox-caption">
              {lightboxItem.title && <div className="shared-gallery-lightbox-title">{lightboxItem.title}</div>}
              {lightboxItem.caption && <div className="shared-gallery-lightbox-text">{lightboxItem.caption}</div>}
            </div>
          )}
        </div>
      )}

      {/* "Powered by AmpNebula" badge for public visitors */}
      {!editMode && (
        <div className="showcase-branding">
          <a href="/" className="showcase-branding-link">
            ⚡ Powered by <strong>AmpNebula</strong>
          </a>
        </div>
      )}

      {/* Gallery Edit Modal (edit mode) */}
      {editingGalleryItem && editMode && (
        <div className="shared-gallery-lightbox" onClick={() => setEditingGalleryItem(null)}>
          <div className="wysiwyg-gallery-modal" onClick={e => e.stopPropagation()}>
            <div className="wysiwyg-gallery-modal-header">
              <h3>Edit Gallery Item</h3>
              <button className="wysiwyg-gallery-modal-close" onClick={() => setEditingGalleryItem(null)}>✕</button>
            </div>
            {editingGalleryItem.item_type === 'youtube' ? (
              <div style={{ position: 'relative' }}>
                <img src={`https://img.youtube.com/vi/${extractYouTubeId(editingGalleryItem.video_url)}/hqdefault.jpg`}
                  alt="YouTube preview" className="wysiwyg-gallery-modal-preview" />
                <div className="shared-gallery-youtube-play-overlay">▶️</div>
              </div>
            ) : (
              <img src={`${API_URL}/showcase/gallery/${editingGalleryItem.gallery_item_id}/image`}
                alt="Preview" className="wysiwyg-gallery-modal-preview" />
            )}
            <div className="wysiwyg-field">
              <label>Title</label>
              <input type="text" maxLength={200} placeholder="e.g. 1959 Les Paul Standard"
                value={galleryEditForm.title}
                onChange={(e) => setGalleryEditForm(f => ({ ...f, title: e.target.value }))} />
            </div>
            <div className="wysiwyg-field">
              <label>Caption</label>
              <textarea placeholder="Tell the story behind this piece…" rows={3}
                value={galleryEditForm.caption}
                onChange={(e) => setGalleryEditForm(f => ({ ...f, caption: e.target.value }))} />
            </div>
            <div className="wysiwyg-field">
              <label>Category</label>
              <select value={galleryEditForm.category}
                onChange={(e) => setGalleryEditForm(f => ({ ...f, category: e.target.value }))}>
                {GALLERY_CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div className="wysiwyg-gallery-modal-footer">
              <button className="wysiwyg-btn-danger" onClick={() => deleteGalleryItem(editingGalleryItem.gallery_item_id)}>
                🗑️ Delete
              </button>
              <button className="wysiwyg-btn wysiwyg-btn-publish" onClick={updateGalleryItem}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SharedCollectionScreen;
